import { Suspense, lazy } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { useSelector } from "react-redux";
import { Redirect, Route, BrowserRouter as Router } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { CSSTransition } from "react-transition-group";

import { RootState } from "store/rootReducer";
import {
  selectApplicationToolsOpen,
  selectOrganizationSettingsOpen,
  usePanelStore
} from "stores/panelStore";
import { retry } from "utils";
import { basePath } from "utils/publicUrl";
import * as Views from "views";

import { useCrashDetectorRoute } from "hooks/useCrashDetectorRoute";

import SSOCallback from "components/auth/SSOCallback";
import OrganizationSettingsLayout from "components/org-settings/OrganizationSettingsLayout";
import FullPost from "components/user-documentation/FullPost";
import { useInitialUserContextLoader } from "components/user/hooks";

import "./App.css";

const Home = lazy(() => retry(() => import("./views/Home")));
const Components = lazy(() => retry(() => import("./views/Components")));

const CrashDetectorRouteTracker = (): null => {
  useCrashDetectorRoute();
  return null;
};

type AppRouterPropsT = {
  doneLoading: boolean;
};
const AppRouter = ({
  //tells the loading screen to redirect to either /app or /login
  doneLoading = false
}: AppRouterPropsT): JSX.Element => {
  // selectors
  const user = useSelector((state: RootState) => state.auth.user);
  const organizationSettingsOpen = usePanelStore(selectOrganizationSettingsOpen);
  const appToolsOpen = usePanelStore(selectApplicationToolsOpen);

  // hooks
  useInitialUserContextLoader();

  const reCaptchaV3SiteKey = "6Lfo0tEgAAAAABh8YWjqn7BcRtWhRyEv3sRrQQlY";

  return (
    <Router basename={basePath()}>
      <CrashDetectorRouteTracker />
      {organizationSettingsOpen ? (
        <OrganizationSettingsLayout authenticated={!!user && user.role === "Admin"} />
      ) : null}
      {appToolsOpen ? <Views.AppTools authenticated={!!user} /> : null}
      <Route exact path="/loading">
        <Views.Loading redirect={doneLoading} />
      </Route>
      <Route exact path="/">
        {user ? (
          <Suspense fallback={<Views.Loading />}>
            <Redirect to="/app" />
          </Suspense>
        ) : (
          <Views.Loading redirect={doneLoading} />
        )}
      </Route>
      <Route exact path="/app">
        {user ? (
          <Suspense fallback={<Views.Loading />}>
            <Home />
          </Suspense>
        ) : (
          <Views.Loading redirect={doneLoading} />
        )}
      </Route>
      <Route path="/user-documentation-post/:id">
        {user ? (
          <Suspense fallback={<Views.Loading />}>
            <FullPost />
          </Suspense>
        ) : (
          <Views.Loading redirect={doneLoading} />
        )}
      </Route>

      <Route path="/popout/chart/:id">
        <Views.PopoutChartPane />
      </Route>
      <Route path="/single/multiphase-chart/:id">
        <Views.SingleComponentMultiphaseChartPane />
      </Route>

      <Route path="/standalone/dashboard/:id">
        <Views.StandaloneDashboard />
      </Route>

      <Route path="/(forgot-password|login|reset-password|mfa-login|signup|admin-signup|auth|sso)">
        <Views.CardWrapper>
          <Route exact path="/forgot-password">
            {({ match }) => {
              return (
                <CSSTransition
                  in={match != null}
                  classNames="page"
                  unmountOnExit
                  timeout={150}>
                  <Views.ForgotPassword />
                </CSSTransition>
              );
            }}
          </Route>
          <Route exact path="/login">
            {({ match }) => (
              <CSSTransition
                in={match != null}
                classNames="page"
                unmountOnExit
                timeout={150}>
                <GoogleReCaptchaProvider reCaptchaKey={reCaptchaV3SiteKey}>
                  <Views.Login />
                </GoogleReCaptchaProvider>
              </CSSTransition>
            )}
          </Route>
          <Route exact path="/sso">
            {({ match }) => (
              <CSSTransition
                in={match != null}
                classNames="page"
                unmountOnExit
                timeout={150}>
                <Views.SSO />
              </CSSTransition>
            )}
          </Route>
          <Route exact path="/auth/sso/callback">
            {({ match }) => (
              <CSSTransition
                in={match != null}
                classNames="page"
                unmountOnExit
                timeout={150}>
                <SSOCallback />
              </CSSTransition>
            )}
          </Route>
          <Route exact path="/mfa-login">
            {({ match }) => (
              <CSSTransition
                in={match != null}
                classNames="page"
                unmountOnExit
                timeout={150}>
                <Views.TwoFactorAuth />
              </CSSTransition>
            )}
          </Route>
          <Route path="/reset-password">
            {({ match, ...props }) => (
              <CSSTransition
                in={match != null}
                classNames="page"
                unmountOnExit
                timeout={300}>
                <Views.ResetPassword {...props} />
              </CSSTransition>
            )}
          </Route>
          <Route exact path="/signup">
            {({ match }) => (
              <CSSTransition
                in={match != null}
                classNames="page"
                unmountOnExit
                timeout={300}>
                <Views.SignUp />
              </CSSTransition>
            )}
          </Route>
          <Route exact path="/admin-signup">
            {({ match }) => (
              <CSSTransition
                in={match != null}
                classNames="page"
                unmountOnExit
                timeout={300}>
                <Views.AdminSignup />
              </CSSTransition>
            )}
          </Route>
        </Views.CardWrapper>
      </Route>
      <Route path="/components">
        <Components />
      </Route>
    </Router>
  );
};

export default AppRouter;
